import {
  containerAlertTypes,
  DatabaseCode,
  formatWithReactIntl,
  siteAlertTypes,
  transportCategories,
  valveTypes,
} from '@ots-frontend/ots-data';
import { type Context, useContext, useMemo } from 'react';
import {
  createIntl,
  createIntlCache,
  IntlContext,
  type IntlShape,
} from 'react-intl';
import { OtsCombobox } from '../components/OtsCombobox';
import { OtsFormFieldSize } from '../components/OtsFormField';
import { OtsMultiSelect } from '../components/OtsMultiSelect';
import type { OtsSelectOption } from '../components/OtsSelect';

export type OtsDatabaseCodeSelectProps = {
  codeType?: string;
  codes?: DatabaseCode[];
  optionItems?: OtsSelectOption[];
  label: string;
  description?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  size?: OtsFormFieldSize;
  id?: string;
};

export type OtsDatabaseCodeMultiSelectProps = Omit<
  OtsDatabaseCodeSelectProps,
  'id' | 'value' | 'onChange'
> & {
  value: string[];
  onChange: (value: string[]) => void;
};

/** The database-code catalogs supported by the legacy selectors. */
export const getDatabaseCodes = (codeType?: string): DatabaseCode[] => {
  if (codeType === 'TRANSPORT_CATEGORY') return transportCategories;
  if (codeType === 'VALVE_TYPE') return valveTypes;
  if (codeType === 'FIELD_ALERT_TYPE') {
    return [
      ...Object.values(containerAlertTypes),
      ...Object.values(siteAlertTypes),
    ];
  }
  if (codeType === 'FIELD_ALERT_DETECTION') {
    return [
      {
        code: 'MANUAL',
        displayValue: {
          id: 'common.entity.fieldalert.detection.manual',
          defaultMessage: 'Manual',
        },
      },
      {
        code: 'AUTO',
        displayValue: {
          id: 'common.entity.fieldalert.detection.auto',
          defaultMessage: 'Automatic',
        },
      },
    ];
  }
  return [];
};

const fallbackIntl = createIntl(
  { locale: 'en', defaultLocale: 'en' },
  createIntlCache()
);

const formatCodeDisplayValue = (intl: IntlShape, code: DatabaseCode) =>
  formatWithReactIntl(intl, {
    ...code.displayValue,
    defaultMessage: code.displayValue.defaultMessage ?? code.code,
  });

const useCodeOptions = (
  codeType?: string,
  codes?: DatabaseCode[],
  optionItems?: OtsSelectOption[]
) => {
  const intl =
    useContext(IntlContext as unknown as Context<IntlShape | undefined>) ??
    fallbackIntl;
  return useMemo(() => {
    if (optionItems) return optionItems;
    return (codes ?? getDatabaseCodes(codeType))
      .map((code) => ({
        value: code.code,
        label: formatCodeDisplayValue(intl, code),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [codeType, codes, intl, optionItems]);
};

/** Searchable single database-code selector. */
export const OtsDatabaseCodeSelect = ({
  codeType,
  codes,
  optionItems,
  label,
  description,
  value,
  onChange,
  disabled,
  size = 'default',
  id = `database-code-${codeType ?? 'custom'}`,
}: OtsDatabaseCodeSelectProps) => {
  const options = useCodeOptions(codeType, codes, optionItems);
  return (
    <OtsCombobox
      id={id}
      label={label}
      description={description}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      size={size}
    />
  );
};

/** Searchable multiple database-code selector. */
export const OtsDatabaseCodeMultiSelect = ({
  codeType,
  codes,
  optionItems,
  label,
  description,
  value,
  onChange,
  disabled,
  size = 'default',
}: OtsDatabaseCodeMultiSelectProps) => {
  const options = useCodeOptions(codeType, codes, optionItems);
  return (
    <OtsMultiSelect
      label={label}
      description={description}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      size={size}
      emptyMessage="No database codes are available."
    />
  );
};
